import type { OnboardingSessionConfig } from '@onefootprint/request-types';
import { UAParser } from 'ua-parser-js';
import { getLogger } from '@/idv2/utils/logger';

const { logInfo } = getLogger({ name: 'idv2', location: 'social-media-browser' });

/** Heuristic detection reasons the per-tenant flag is allowed to suppress. */
const SUPPRESSABLE_REASONS = new Set(['platform_webview_in_iframe', 'generic_webview_in_iframe']);

const socialMediaTerms = [
  // Facebook
  'fban',
  'fbav',
  'fbios',
  'fb_iab',
  'fb4a',
  'facebook',
  // Instagram
  'instagram',
  // Twitter/X
  'twitter',
  'twitterbot',
  // LinkedIn
  'linkedin',
  // Snapchat
  'snapchat',
  // TikTok
  'tiktok',
  'bytedance',
  'bytelocale',
  'bytedancewebview',
  'musical_ly',
  // Pinterest
  'pinterest',
  // Messaging apps with in-app browsers
  'whatsapp',
  'line/',
  'wechat',
  'weixin',
  'kakaotalk',
  'telegram',
  // Threads (Meta)
  'threads',
];

const checkIsIframe = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.self !== window.top;
  } catch (_e) {
    return true;
  }
};

/** Check for in-app browser specific JavaScript objects */
const checkInAppBrowserFeatures = () => {
  if (typeof window === 'undefined') return false;

  // Facebook exposes this in their in-app browser
  if ('__fbNative' in window) return true;

  return false;
};

/** Detect Android WebView patterns */
const isAndroidWebView = (ua: string) => {
  const lower = ua.toLowerCase();
  if (!lower.includes('android')) return false;

  // Android WebView includes "; wv)" in the UA
  return lower.includes('; wv)');
};

/** Detect iOS WebView patterns */
export const isIOSWebView = (ua: string) => {
  const lower = ua.toLowerCase();
  const isIOS = lower.includes('iphone') || lower.includes('ipad') || lower.includes('ipod');
  if (!isIOS) return false;

  // iOS WebView uses AppleWebKit but doesn't include "Safari/" at the end
  // Regular Safari includes "Safari/XXX.X.X" at the end of the UA
  return lower.includes('applewebkit') && !lower.includes('safari/');
};

type SocialMediaCheckArgs = {
  ua: string;
  browserName?: string;
  browserVersion?: string;
  isMobile?: boolean;
  isIframe?: boolean;
};

/**
 * Returns a textual reason describing which signal identified a social-media in-app browser, or
 * `null` if none matched. Reasons in `SUPPRESSABLE_REASONS` are heuristic guesses the
 * per-tenant flag may suppress; the rest are explicit identifications.
 */
export const socialMediaCheck = ({
  ua,
  browserName,
  browserVersion,
  isMobile,
  isIframe,
}: SocialMediaCheckArgs): string | null => {
  const lowUserAgent = ua.toLowerCase();
  const lowBrowserName = browserName?.toLowerCase();
  const lowBrowserVersion = browserVersion?.toLowerCase();

  // Check for in-app browser specific JavaScript objects (e.g., Facebook's __fbNative)
  if (checkInAppBrowserFeatures()) {
    return 'in_app_browser_feature:__fbNative';
  }

  // Check if the user agent or browser name/version include any of the social media terms
  if (socialMediaTerms.some(term => lowUserAgent.includes(term))) {
    return 'user_agent_term';
  }
  if (lowBrowserName && socialMediaTerms.some(term => lowBrowserName.includes(term))) {
    return 'browser_name_term';
  }
  if (lowBrowserVersion && socialMediaTerms.some(term => lowBrowserVersion.includes(term))) {
    return 'browser_version_term';
  }

  // Heuristic checks (mobile only) — these are guesses, not explicit identifications.
  if (isMobile) {
    // If we're in a native webview inside an iframe, it's likely a social media in-app browser
    // (cannot be both a native webview and iframe simultaneously)
    if ((isAndroidWebView(ua) || isIOSWebView(ua)) && isIframe) {
      return 'platform_webview_in_iframe';
    }

    // Fallback: running inside an iframe with a generic webview user agent
    const isGenericWebview =
      lowUserAgent.includes('webview') || lowBrowserName?.includes('webview') || lowBrowserVersion?.includes('webview');
    if (isGenericWebview && isIframe) {
      return 'generic_webview_in_iframe';
    }
  }

  return null;
};

/**
 * Detects whether onboarding is running inside a social-media in-app browser. When the
 * `disableSocialMediaWebviewHeuristic` flag is set on the onboarding config, the heuristic
 * webview branches are ignored — explicit identifications (UA terms, `__fbNative`) still apply.
 * Logs the matched reason (and tenant) on every detection, flagging heuristics suppressed by the
 * flag.
 */
const checkIsSocialMediaBrowser = (config?: OnboardingSessionConfig) => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  const uaParser = new UAParser();
  const browser = uaParser.getBrowser();
  const browserName = browser.name;
  const browserVersion = browser.version;
  const device = uaParser.getDevice();
  const isMobile = device.type === 'mobile';
  const isIframe = checkIsIframe();

  const reason = socialMediaCheck({
    ua,
    browserName,
    browserVersion,
    isMobile,
    isIframe,
  });
  if (!reason) {
    return false;
  }

  const isSuppressed = Boolean(config?.flags?.disableSocialMediaWebviewHeuristic) && SUPPRESSABLE_REASONS.has(reason);
  logInfo('Detected social media browser', { reason, isSuppressed });

  if (isSuppressed) {
    // The branch would have matched, but we opted out of it for this tenant.
    return false;
  }

  return true;
};

export default checkIsSocialMediaBrowser;
